// Epoch 35: CSR Campaigns Management Page
// Shows all campaigns assigned to or created by the current CSR
// Date: August 17, 2025

import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase/client';
import { useNavigate } from 'react-router-dom';
import { useAuthSession } from '../../hooks/useAuthSession';
import PresetFilterModal from '../../components/modals/PresetFilterModal';

interface CSRCampaign {
  id: string;
  name: string;
  status: 'draft' | 'submitted' | 'running' | 'completed' | 'paused' | 'cancelled';
  user_id: string;
  user_email: string;
  user_full_name: string;
  company_id: string;
  company_name: string;
  service_tier: string;
  created_at: string;
  created_by: string;
  created_for: string;
  creation_context: string;
  dashboard_url?: string;
  total_leads?: number;
  responses_count?: number;
  // Lead source fields
  lead_source_type?: 'preset_filters' | 'csv_upload' | 'lead_generator' | 'past_list';
  lead_source_id?: string;
  target_audience_type?: string; // Legacy field
  csv_upload?: CSVUploadData;
  preset_filter?: PresetFilterData;
}

interface CSVUploadData {
  id: string;
  campaign_id: string;
  file_name: string;
  file_path: string;
  row_count?: number;
  file_size?: number;
  created_at: string;
}

interface PresetFilterData {
  id: string;
  campaign_id: string;
  request_name: string;
  filters: any;
  status: string;
  created_at: string;
}

type SortField = 'name' | 'user' | 'status' | 'responses' | 'created';
type SortDirection = 'asc' | 'desc';

const CSRCampaigns: React.FC = () => {
  const [campaigns, setCampaigns] = useState<CSRCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCompany, setFilterCompany] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('created');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedFilterModal, setSelectedFilterModal] = useState<CSRCampaign | null>(null);
  const navigate = useNavigate();
  const { session } = useAuthSession();

  const loadCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      
      // First, get companies assigned to this CSR through company_csrs table
      const { data: companyAssignments } = await supabase
        .from('company_csrs')
        .select('company_id')
        .eq('csr_id', session?.user?.id);

      // Don't return early - CSR might have directly assigned campaigns even without company assignments
      const companyIds = companyAssignments?.map(ca => ca.company_id) || [];
      
      // Build the OR filter to include:
      // 1. Campaigns directly assigned to this CSR
      // 2. Campaigns from companies assigned to this CSR
      // 3. Campaigns created by this CSR
      let orFilter = `assigned_csr.eq.${session?.user?.id},created_by.eq.${session?.user?.id}`;
      if (companyIds.length > 0) {
        // Format company IDs with quotes for the IN clause
        const formattedIds = companyIds.map(id => `"${id}"`).join(',');
        orFilter = `assigned_csr.eq.${session?.user?.id},company_id.in.(${formattedIds}),created_by.eq.${session?.user?.id}`;
      }
      
      // Get campaigns from users in these companies OR directly assigned to CSR OR created by this CSR
      const { data: campaignsData, error: campaignsError } = await supabase
        .from('campaigns')
        .select(`
          *,
          users:user_id (
            id,
            email,
            full_name,
            company_id
          ),
          companies:company_id (
            id,
            name
          ),
          preset_filter_requests (
            id,
            request_name,
            filters,
            status,
            target_lead_count
          )
        `)
        .or(orFilter)
        .order('created_at', { ascending: false });

      if (campaignsError) {
        console.error('Error loading campaigns:', campaignsError);
        throw campaignsError;
      }

      // Get response counts for each campaign
      const campaignIds = campaignsData?.map(c => c.id) || [];
      const { data: responseCounts } = await supabase
        .from('lead_responses')
        .select('campaign_id')
        .in('campaign_id', campaignIds);

      // Get CSV uploads for campaigns
      const { data: csvUploads } = await supabase
        .from('campaign_lead_uploads')
        .select('*')
        .in('campaign_id', campaignIds);

      // Get preset filter requests for campaigns
      const { data: presetFilters } = await supabase
        .from('preset_filter_requests')
        .select('*')
        .in('campaign_id', campaignIds);

      // Get lead_source_ids for Previous Target Lists
      const leadSourceIds = campaignsData
        ?.filter(c => c.lead_source_type === 'past_list' && c.lead_source_id)
        .map(c => c.lead_source_id) || [];
      
      // Get filters by id (for Previous Target Lists)
      const { data: presetFiltersById } = leadSourceIds.length > 0 ? await supabase
        .from('preset_filter_requests')
        .select('*')
        .in('id', leadSourceIds) : { data: [] };

      // Transform data
      const transformedCampaigns = campaignsData?.map(campaign => {
        const responseCount = responseCounts?.filter(r => r.campaign_id === campaign.id).length || 0;
        const csvUpload = csvUploads?.find(csv => csv.campaign_id === campaign.id);
        
        // For regular campaigns, use campaign_id
        let presetFilter = presetFilters?.find(pf => pf.campaign_id === campaign.id);
        
        // For Previous Target Lists, use lead_source_id
        if (campaign.lead_source_type === 'past_list' && campaign.lead_source_id && !presetFilter) {
          presetFilter = presetFiltersById?.find(pf => pf.id === campaign.lead_source_id);
        }
        
        return {
          id: campaign.id,
          name: campaign.name,
          status: campaign.status,
          user_id: campaign.users?.id || campaign.user_id,
          user_email: campaign.users?.email || '',
          user_full_name: campaign.users?.full_name || '',
          company_id: campaign.companies?.id || campaign.company_id,
          company_name: campaign.companies?.name || '',
          service_tier: campaign.service_tier,
          created_at: campaign.created_at,
          created_by: campaign.created_by,
          created_for: campaign.created_for,
          creation_context: campaign.creation_context,
          dashboard_url: campaign.dashboard_url,
          total_leads: campaign.total_leads,
          responses_count: responseCount,
          // Lead source information
          lead_source_type: campaign.lead_source_type,
          lead_source_id: campaign.lead_source_id,
          target_audience_type: campaign.target_audience_type,
          csv_upload: csvUpload,
          preset_filter: presetFilter,
          preset_filter_requests: presetFilter ? [presetFilter] : campaign.preset_filter_requests || []
        };
      }) || [];

      setCampaigns(transformedCampaigns);
    } catch (error) {
      console.error('Error loading campaigns:', error);
    } finally {
      setLoading(false);
    }
  }, [session?.user?.id]);

  useEffect(() => {
    if (session?.user?.id) {
      loadCampaigns();
    }
  }, [session?.user?.id, loadCampaigns]);

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'submitted': return 'bg-yellow-100 text-yellow-800';
      case 'running': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'paused': return 'bg-orange-100 text-orange-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft': return '📝';
      case 'submitted': return '⏳';
      case 'running': return '🚀';
      case 'completed': return '✅';
      case 'paused': return '⏸️';
      case 'cancelled': return '🚫';
      default: return '📋';
    }
  };

  const getLeadSourceDisplay = (campaign: any) => {
    if (campaign.lead_acquisition_method === 'preset_filters') {
      const targetList = campaign.preset_filter_requests?.[0];
      if (targetList) {
        return (
          <div className="text-sm">
            <div>Target Lead List</div>
            <div className="text-xs text-gray-500">{targetList.request_name}</div>
          </div>
        );
      }
      return 'Target Lead List';
    } else if (campaign.lead_acquisition_method === 'past_list') {
      const targetList = campaign.preset_filter_requests?.[0];
      if (targetList) {
        return (
          <div className="text-sm">
            <div>Previous List</div>
            <div className="text-xs text-gray-500">{targetList.request_name}</div>
          </div>
        );
      }
      return 'Previous Target List';
    } else if (campaign.lead_acquisition_method === 'csv_upload') {
      return 'CSV Upload';
    } else if (campaign.target_audience_type === 'csv_upload') {
      return 'CSV Upload';
    }
    return '-';
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if clicking same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortCampaigns = (campaignsToSort: CSRCampaign[]) => {
    return [...campaignsToSort].sort((a, b) => {
      let compareValue = 0;
      
      switch (sortField) {
        case 'name':
          compareValue = a.name.localeCompare(b.name);
          break;
        case 'user': {
          const aUser = a.user_full_name || a.user_email;
          const bUser = b.user_full_name || b.user_email;
          compareValue = aUser.localeCompare(bUser);
          break;
        }
        case 'status':
          compareValue = a.status.localeCompare(b.status);
          break;
        case 'responses':
          compareValue = (a.responses_count || 0) - (b.responses_count || 0);
          break;
        case 'created':
          compareValue = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
      }
      
      return sortDirection === 'asc' ? compareValue : -compareValue;
    });
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          campaign.user_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          campaign.company_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || campaign.status === filterStatus;
    const matchesCompany = filterCompany === 'all' || campaign.company_id === filterCompany;
    return matchesSearch && matchesStatus && matchesCompany;
  });

  const sortedCampaigns = sortCampaigns(filteredCampaigns);

  const uniqueCompanies = Array.from(new Set(campaigns.map(c => c.company_id)))
    .map(id => ({
      id,
      name: campaigns.find(c => c.company_id === id)?.company_name || ''
    }))
    .filter(c => c.name);

  const handleViewCampaign = (campaign: CSRCampaign) => {
    if (campaign.dashboard_url) {
      window.open(campaign.dashboard_url, '_blank');
    } else {
      navigate(`/campaigns/${campaign.id}`);
    }
  };

  const handleDownloadCSV = async (campaign: CSRCampaign) => {
    if (!campaign.csv_upload?.file_path) {
      alert('No CSV file available for this campaign');
      return;
    }

    try {
      // Get a signed URL for download
      const { data, error } = await supabase.storage
        .from('lead-uploads')
        .download(campaign.csv_upload.file_path);

      if (error) throw error;

      // Create a blob and trigger download
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = campaign.csv_upload.file_name || 'leads.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      alert('Failed to download CSV file. Please check your permissions.');
    }
  };

  const handleViewPresetFilter = (campaign: CSRCampaign) => {
    // Use preset_filter directly (works for both preset_filters and past_list)
    if (campaign.preset_filter) {
      setSelectedFilterModal(campaign);
    } else {
      console.error('No filter data found for campaign:', campaign);
      alert('Filter data not found. The lead_source_id may not be set for this campaign.');
      return;
    }
  };

  const handleApproveCampaign = async (campaignId: string) => {
    try {
      // Get current user session for approved_by
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        alert('Session expired. Please refresh the page.');
        return;
      }

      // Get campaign details for notification
      const campaign = campaigns.find(c => c.id === campaignId);
      if (!campaign) {
        console.error('Campaign not found for notification');
        return;
      }

      // Update campaign status
      const { error } = await supabase
        .from('campaigns')
        .update({ 
          status: 'running',
          started_at: new Date().toISOString(),
          approved_at: new Date().toISOString(),
          approved_by: session.user.id
        })
        .eq('id', campaignId);

      if (error) throw error;
      
      // Create notification for the campaign owner
      const { error: notifError } = await supabase
        .from('notifications')
        .insert({
          user_id: campaign.user_id,
          type: 'campaign_approved',
          title: 'Campaign Approved!',
          message: `Your campaign "${campaign.name}" has been approved and is now running.`,
          priority: 'high',
          read: false,
          data: {
            campaign_id: campaignId,
            campaign_name: campaign.name,
            action_url: '/campaigns'
          }
        });

      if (notifError) {
        console.error('Failed to create approval notification:', notifError);
        // Don't fail the approval, just log the error
      }
      
      // Reload campaigns
      await loadCampaigns();
      alert('Campaign approved successfully!');
    } catch (error) {
      console.error('Error approving campaign:', error);
      alert('Failed to approve campaign');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">My Assigned Campaigns</h1>
                <p className="mt-1 text-sm text-gray-600">
                  Manage all campaigns you're assigned to or have created on behalf of users
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="text-2xl font-bold text-purple-600">{campaigns.length}</div>
          <div className="text-sm text-gray-600">Total Campaigns</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="text-2xl font-bold text-yellow-600">
            {campaigns.filter(c => c.status === 'submitted').length}
          </div>
          <div className="text-sm text-gray-600">Awaiting Approval</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="text-2xl font-bold text-green-600">
            {campaigns.filter(c => c.status === 'running').length}
          </div>
          <div className="text-sm text-gray-600">Active Campaigns</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="text-2xl font-bold text-blue-600">
            {uniqueCompanies.length}
          </div>
          <div className="text-sm text-gray-600">Companies</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <input
              type="text"
              placeholder="Search campaigns, users, or companies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
              <option value="running">Running</option>
              <option value="completed">Completed</option>
              <option value="paused">Paused</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <select
              value={filterCompany}
              onChange={(e) => setFilterCompany(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Companies</option>
              {uniqueCompanies.map(company => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Campaigns List */}
      {sortedCampaigns.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <div className="text-gray-400 text-6xl mb-4">📭</div>
          <h3 className="text-xl font-medium text-gray-900 mb-2">No campaigns found</h3>
          <p className="text-gray-500">
            {searchTerm || filterStatus !== 'all' || filterCompany !== 'all'
              ? 'Try adjusting your filters'
              : 'You have no assigned campaigns yet'}
          </p>
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center">
                    Campaign
                    <span className="ml-1">
                      {sortField === 'name' && (
                        sortDirection === 'asc' ? '▲' : '▼'
                      )}
                      {sortField !== 'name' && <span className="text-gray-300">⇅</span>}
                    </span>
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('user')}
                >
                  <div className="flex items-center">
                    User / Company
                    <span className="ml-1">
                      {sortField === 'user' && (
                        sortDirection === 'asc' ? '▲' : '▼'
                      )}
                      {sortField !== 'user' && <span className="text-gray-300">⇅</span>}
                    </span>
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center">
                    Status
                    <span className="ml-1">
                      {sortField === 'status' && (
                        sortDirection === 'asc' ? '▲' : '▼'
                      )}
                      {sortField !== 'status' && <span className="text-gray-300">⇅</span>}
                    </span>
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('responses')}
                >
                  <div className="flex items-center">
                    Responses
                    <span className="ml-1">
                      {sortField === 'responses' && (
                        sortDirection === 'asc' ? '▲' : '▼'
                      )}
                      {sortField !== 'responses' && <span className="text-gray-300">⇅</span>}
                    </span>
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('created')}
                >
                  <div className="flex items-center">
                    Created
                    <span className="ml-1">
                      {sortField === 'created' && (
                        sortDirection === 'asc' ? '▲' : '▼'
                      )}
                      {sortField !== 'created' && <span className="text-gray-300">⇅</span>}
                    </span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lead Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedCampaigns.map((campaign) => (
                <tr key={campaign.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="max-w-xs">
                      <div className="text-sm font-medium text-gray-900 truncate" title={campaign.name}>
                        {campaign.name.length > 50 ? `${campaign.name.substring(0, 47)}...` : campaign.name}
                      </div>
                      {/* EPOCH 63: Renamed "Junior SDR" to "Platform" */}
                      <div className="text-sm text-gray-500">
                        {campaign.service_tier === 'junior_sdr' ? 'Platform' : 'Top Qualifier'}
                      </div>
                      {campaign.creation_context === 'csr_assisted' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 mt-1">
                          🎭 CSR Created
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm text-gray-900">
                        {campaign.user_full_name || campaign.user_email}
                      </div>
                      <div className="text-sm text-gray-500">
                        {campaign.company_name}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(campaign.status)}`}>
                      <span className="mr-1">{getStatusIcon(campaign.status)}</span>
                      {campaign.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {campaign.responses_count || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(campaign.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      {/* Show lead source type and action button */}
                      {campaign.csv_upload && (
                        <div>
                          <div className="text-gray-900 font-medium">CSV Upload</div>
                          <button
                            onClick={() => handleDownloadCSV(campaign)}
                            className="text-blue-600 hover:text-blue-900 text-xs"
                          >
                            📥 Download ({campaign.csv_upload.row_count || 0} rows)
                          </button>
                        </div>
                      )}
                      {(campaign.lead_acquisition_method === 'preset_filters' || campaign.preset_filter) && (
                        <div>
                          <div className="text-gray-900 font-medium">Target Leads</div>
                          <button
                            onClick={() => handleViewPresetFilter(campaign)}
                            className="text-blue-600 hover:text-blue-900 text-xs"
                          >
                            🔍 View Filters
                          </button>
                        </div>
                      )}
                      {campaign.lead_acquisition_method === 'past_list' && (
                        <div>
                          <div className="text-gray-900 font-medium">Previous Target List</div>
                          {campaign.preset_filter && (
                            <button
                              onClick={() => handleViewPresetFilter(campaign)}
                              className="text-blue-600 hover:text-blue-900 text-xs"
                            >
                              🔍 View Filters
                            </button>
                          )}
                        </div>
                      )}
                      {campaign.lead_source_type === 'lead_generator' && (
                        <div className="text-gray-900">Lead Generator</div>
                      )}
                      {campaign.lead_source_type === 'past_list' && (
                        <div>
                          <div className="text-gray-900">Previous Target Lead List</div>
                          {campaign.preset_filter && (
                            <button
                              onClick={() => handleViewPresetFilter(campaign)}
                              className="text-blue-600 hover:text-blue-900 text-xs"
                            >
                              🔍 View Filters
                            </button>
                          )}
                        </div>
                      )}
                      {!campaign.lead_source_type && !campaign.csv_upload && !campaign.preset_filter && (
                        <div className="text-gray-400 italic">Not specified</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewCampaign(campaign)}
                        className="text-purple-600 hover:text-purple-900"
                      >
                        View
                      </button>
                      {campaign.status === 'submitted' && (
                        <button
                          onClick={() => handleApproveCampaign(campaign.id)}
                          className="text-green-600 hover:text-green-900"
                        >
                          Approve
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>

      {/* Preset Filter Modal */}
      {selectedFilterModal?.preset_filter && (
        <PresetFilterModal
          isOpen={!!selectedFilterModal}
          onClose={() => setSelectedFilterModal(null)}
          filterData={selectedFilterModal.preset_filter}
          campaignName={selectedFilterModal.name}
        />
      )}
    </div>
  );
};

export default CSRCampaigns;